import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { useOnlineStatus } from './hooks/useOnlineStatus'
import { usePermissions } from './hooks/usePermissions'

// Layouts
import AuthLayout from './components/layouts/AuthLayout'
import MobileLayout from './components/layouts/MobileLayout'
import WebLayout from './components/layouts/WebLayout'

// Auth pages
import LoginPage from './pages/auth/LoginPage'

// User pages (PWA/Mobile)
import PersonalDashboard from './pages/user/PersonalDashboard'
import ScoresPage from './pages/user/ScoresPage'
import ProfilePage from './pages/user/ProfilePage'
import AttendancePage from './pages/user/AttendancePage'
import LeavePage from './pages/user/LeavePage'
import LearningPage from './pages/user/LearningPage'

// KPI mobile pages
import KPIHubPage from './pages/kpi/KPIHubPage'
import MoMSubmissionPage from './pages/kpi/MoMSubmissionPage'
import KPICalendarPage from './pages/kpi/KPICalendarPage'
import IndicatorSubmissionPage from './pages/kpi/IndicatorSubmissionPage'
import C1SubmissionPage from './pages/kpi/C1SubmissionPage'
import C2SubmissionPage from './pages/kpi/C2SubmissionPage'
import C3SubmissionPage from './pages/kpi/C3SubmissionPage'
import C4SubmissionPage from './pages/kpi/C4SubmissionPage'
import C5SubmissionPage from './pages/kpi/C5SubmissionPage'
import C6SubmissionPage from './pages/kpi/C6SubmissionPage'
import C7SubmissionPage from './pages/kpi/C7SubmissionPage'
import C8SubmissionPage from './pages/kpi/C8SubmissionPage'

// Projects (PWA)
import ProjectsHubPage from './pages/projects/ProjectsHubPage'
import ProjectMobilePage from './pages/projects/ProjectMobilePage'

// Admin pages (Web)
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import UsersPage from './pages/admin/UsersPage'
import ReportsPage from './pages/admin/ReportsPage'
import CompanySettingsPage from './pages/admin/CompanySettingsPage'
import IndicatorsPage from './pages/admin/IndicatorsPage'
import HRManagementPage from './pages/admin/HRManagementPage'
import LearningAnalyticsPage from './pages/admin/LearningAnalyticsPage'
import ProjectsListPage from './pages/admin/projects/ProjectsListPage'
import ProjectDetailPage from './pages/admin/projects/ProjectDetailPage'

// Superadmin pages (Web)
import SuperadminDashboardPage from './pages/superadmin/SuperadminDashboardPage'
import CompaniesPage from './pages/superadmin/CompaniesPage'

// Common pages
import NotificationsPage from './pages/NotificationsPage'

// Components
import OfflineIndicator from './components/common/OfflineIndicator'
import LoadingSpinner from './components/common/LoadingSpinner'
import ImpersonationBanner from './components/common/ImpersonationBanner'
// UpdatePrompt removed - using autoUpdate

// Detect if running as installed PWA (standalone mode)
function isPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    || (navigator as unknown as { standalone?: boolean }).standalone === true
}

type PermUser = { role: string; permissions?: Record<string, Record<string, boolean>> | null } | null

// Check if user has any admin page permissions (via designation config)
function hasAdminAccess(user: PermUser): boolean {
  if (!user) return false
  if (user.role === 'superadmin' || user.role === 'company_admin') return true
  if (!user.permissions) return false
  return Object.entries(user.permissions).some(
    ([key, perms]) => key.startsWith('admin_') && perms?.view === true
  )
}

// Check if user has any PWA page permissions
function hasPWAAccess(user: PermUser): boolean {
  if (!user) return false
  if (user.role === 'superadmin') return true
  if (!user.permissions) return true // fallback: PWA access allowed by default
  return Object.entries(user.permissions).some(
    ([key, perms]) => key.startsWith('pwa_') && perms?.view === true
  )
}

function getPwaLandingPath(canAccessPage: (module: string) => boolean): string | null {
  const destinations = [
    { module: 'pwa_learning', path: '/learning' },
    { module: 'pwa_attendance', path: '/attendance' },
    { module: 'pwa_leave', path: '/leave' },
    { module: 'pwa_kpi_hub', path: '/kpi' },
    { module: 'pwa_kpi_submission', path: '/kpi/mom' },
    { module: 'pwa_project_monitoring', path: '/projects' },
    { module: 'pwa_scores', path: '/scores' },
    { module: 'pwa_timelog', path: '/dashboard' },
    { module: 'pwa_dashboard', path: '/dashboard' },
  ]
  return destinations.find(({ module }) => canAccessPage(module))?.path || null
}

// Default redirect based on role + permissions
function DefaultRedirect() {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const { canAccessPage } = usePermissions()
  const pwaLandingPath = getPwaLandingPath(canAccessPage)

  // Wait for checkAuth to finish so we have fresh permissions
  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // PWA standalone mode opens the first enabled page rather than a disabled dashboard.
  if (isPWA()) {
    return <Navigate to={pwaLandingPath || '/profile'} replace />
  }

  // Superadmin goes to superadmin panel
  if (user?.role === 'superadmin') {
    return <Navigate to="/superadmin" replace />
  }

  // Users with any admin permission go to admin panel
  if (hasAdminAccess(user)) {
    return <Navigate to="/admin" replace />
  }

  // Users without any PWA page access go to the admin HR page.
  if (!hasPWAAccess(user)) {
    return <Navigate to="/admin/hr" replace />
  }

  // Everyone else goes to PWA app
  return <Navigate to="/dashboard" replace />
}

// Protected route wrapper
function ProtectedRoute({ children, allowedRoles, requireAdminAccess }: {
  children: React.ReactNode
  allowedRoles?: string[]
  requireAdminAccess?: boolean
}) {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const { canAccessPage } = usePermissions()
  const pwaLandingPath = getPwaLandingPath(canAccessPage) || '/profile'

  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Permission-based admin access check
  if (requireAdminAccess && user && !hasAdminAccess(user)) {
    return <Navigate to={hasPWAAccess(user) ? pwaLandingPath : "/admin/hr"} replace />
  }

  // Check role restrictions
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // In PWA mode, always redirect to mobile view
    if (isPWA()) {
      return <Navigate to={pwaLandingPath} replace />
    }
    if (user.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />
    } else if (hasAdminAccess(user)) {
      return <Navigate to="/admin" replace />
    } else {
      return <Navigate to={hasPWAAccess(user) ? pwaLandingPath : "/admin/hr"} replace />
    }
  }

  return <>{children}</>
}

// PWA route guard - users with PWA permissions (in PWA mode, all authenticated users get access)
function PWARoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore()
  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }
  // In PWA standalone mode, allow all authenticated users
  if (!isPWA() && user && !hasPWAAccess(user)) {
    if (hasAdminAccess(user)) {
      return <Navigate to="/admin" replace />
    }
    return <Navigate to="/admin/hr" replace />
  }
  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  )
}

function PwaLandingRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore()
  const { canAccessPage } = usePermissions()

  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <Navigate to={getPwaLandingPath(canAccessPage) || '/profile'} replace />
}

// Web route guard - users with admin permissions (blocked in PWA mode)
function AdminRoute({ children }: { children: React.ReactNode }) {
  if (isPWA()) {
    return <PwaLandingRedirect />
  }
  return (
    <ProtectedRoute requireAdminAccess>
      {children}
    </ProtectedRoute>
  )
}

// Web route guard - only for superadmin (blocked in PWA mode)
function SuperadminRoute({ children }: { children: React.ReactNode }) {
  if (isPWA()) {
    return <PwaLandingRedirect />
  }
  return (
    <ProtectedRoute allowedRoles={['superadmin']}>
      {children}
    </ProtectedRoute>
  )
}

function RequireDashboardAccess({ children }: { children: React.ReactNode }) {
  const { canAccessPage } = usePermissions()
  const canViewDashboard = [
    'pwa_dashboard',
    'pwa_attendance',
    'pwa_timelog',
    'pwa_kpi_hub',
    'pwa_kpi_submission',
    'pwa_scores',
  ].some(canAccessPage)

  if (!canViewDashboard) {
    return <Navigate to={getPwaLandingPath(canAccessPage) || '/profile'} replace />
  }
  return <>{children}</>
}

// Permission guard - redirects to the first enabled PWA module when Learning is disabled.
function RequireLearningAccess({ children }: { children: React.ReactNode }) {
  const { canAccessPage } = usePermissions()
  const { user } = useAuthStore()
  if (!canAccessPage('pwa_learning')) {
    const fallbackPath = getPwaLandingPath(canAccessPage)
    return <Navigate to={fallbackPath || (hasPWAAccess(user) ? '/profile' : '/admin/hr')} replace />
  }
  return <>{children}</>
}

function RequirePermission({ module, children }: { module: string; children: React.ReactNode }) {
  const { canAccessPage } = usePermissions()
  const { user } = useAuthStore()
  if (!canAccessPage(module)) {
    if (module.startsWith('pwa_')) {
      return <Navigate to={getPwaLandingPath(canAccessPage) || '/profile'} replace />
    }
    // Preserve the existing web fallback when an admin permission is missing.
    if (!isPWA() && !hasPWAAccess(user)) {
      return <Navigate to="/admin/hr" replace />
    }
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

function App() {
  const isOnline = useOnlineStatus()

  return (
    <>
      <ImpersonationBanner />
      {!isOnline && <OfflineIndicator />}

      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* PWA Routes - Supervisor/User only */}
        <Route
          element={
            <PWARoute>
              <MobileLayout />
            </PWARoute>
          }
        >
          {/* Main PWA pages */}
          <Route path="/kpi" element={<RequirePermission module="pwa_kpi_hub"><KPIHubPage /></RequirePermission>} />
          <Route path="/dashboard" element={<RequireDashboardAccess><PersonalDashboard /></RequireDashboardAccess>} />
          <Route path="/scores" element={<RequirePermission module="pwa_scores"><ScoresPage /></RequirePermission>} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/attendance" element={<RequirePermission module="pwa_attendance"><AttendancePage /></RequirePermission>} />
          <Route path="/leave" element={<RequirePermission module="pwa_leave"><LeavePage /></RequirePermission>} />
          <Route path="/learning" element={<RequireLearningAccess><LearningPage /></RequireLearningAccess>} />
          <Route path="/kpi/calendar" element={<RequirePermission module="pwa_kpi_hub"><KPICalendarPage /></RequirePermission>} />
          <Route path="/kpi/mom" element={<RequirePermission module="pwa_kpi_submission"><MoMSubmissionPage /></RequirePermission>} />

          {/* KPI Submission pages */}
          <Route path="/kpi/submit/c-1" element={<RequirePermission module="pwa_kpi_submission"><C1SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-2" element={<RequirePermission module="pwa_kpi_submission"><C2SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-3" element={<RequirePermission module="pwa_kpi_submission"><C3SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-4" element={<RequirePermission module="pwa_kpi_submission"><C4SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-5" element={<RequirePermission module="pwa_kpi_submission"><C5SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-6" element={<RequirePermission module="pwa_kpi_submission"><C6SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-7" element={<RequirePermission module="pwa_kpi_submission"><C7SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/c-8" element={<RequirePermission module="pwa_kpi_submission"><C8SubmissionPage /></RequirePermission>} />
          <Route path="/kpi/submit/:code" element={<RequirePermission module="pwa_kpi_submission"><IndicatorSubmissionPage /></RequirePermission>} />

          {/* Projects (PWA) */}
          <Route path="/projects" element={<RequirePermission module="pwa_project_monitoring"><ProjectsHubPage /></RequirePermission>} />
          <Route path="/projects/:id" element={<RequirePermission module="pwa_project_monitoring"><ProjectMobilePage /></RequirePermission>} />

          {/* Notifications */}
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>

        {/* HR page - accessible to all authenticated users (fallback landing page) */}
        <Route
          element={
            <ProtectedRoute>
              <WebLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/admin/hr" element={<HRManagementPage />} />
        </Route>

        {/* Web Routes - Company Admin only */}
        <Route
          element={
            <AdminRoute>
              <WebLayout />
            </AdminRoute>
          }
        >
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/indicators" element={<IndicatorsPage />} />
          <Route path="/admin/reports" element={<ReportsPage />} />
          <Route path="/admin/settings" element={<CompanySettingsPage />} />
          <Route path="/admin/learning" element={<RequirePermission module="admin_learning_analytics"><LearningAnalyticsPage /></RequirePermission>} />
          <Route path="/admin/projects" element={<RequirePermission module="admin_project_monitoring"><ProjectsListPage /></RequirePermission>} />
          <Route path="/admin/projects/:id" element={<RequirePermission module="admin_project_monitoring"><ProjectDetailPage /></RequirePermission>} />
          <Route path="/admin/profile" element={<ProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>

        {/* Web Routes - Superadmin only */}
        <Route
          element={
            <SuperadminRoute>
              <WebLayout />
            </SuperadminRoute>
          }
        >
          <Route path="/superadmin" element={<SuperadminDashboardPage />} />
          <Route path="/superadmin/companies" element={<CompaniesPage />} />
          <Route path="/superadmin/profile" element={<ProfilePage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<DefaultRedirect />} />
        <Route path="*" element={<DefaultRedirect />} />
      </Routes>
    </>
  )
}

export default App
